import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumb from '../../components/ui/Breadcrumb';
import SectorTable from './components/SectorTable';
import MemberAllocationTable from './components/MemberAllocationTable';
import MemberPreviewModal from './components/MemberPreviewModal';
import EditServiceModal from './components/EditServiceModal';
import RemoveMemberModal from './components/RemoveMemberModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import httpHelper, { Method_TYPE } from 'utils/http-helper';
import URL_CONSTANTS from 'constants/url-constants';
import { AwardIcon } from 'lucide-react';
import { set } from 'date-fns';
import { calculateAge } from 'utils/calculateAge';
import { json } from 'd3';

const AllocatedMembersManagement = () => {
  const [sectors, setSectors] = useState([]);
  const [currentBatch, setCurrentBatch] = useState('');
  const [currentBatchObj, setCurrentBatchObj] = useState({});
  const [showSectorInfo, setShowSectorInfo] = useState(true);
  const [statistics,setStatistics] = useState({});
  const [selectedSector, setSelectedSector] = useState(null);
  const [allocatedMembers, setAllocatedMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [previewMember, setPreviewMember] = useState(null);
  const [groupLeader,setGroupLeader]= useState(null);
  const [groupLeaders, setGroupLeaders] = useState([]);
  const [editServiceMember, setEditServiceMember] = useState(null);
  const [selectedMember, setSelectedMember] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [unallocatingServiceId, setUnallocatingServiceId] = useState(null);

  // Mock data for sectors
  const mockSectors = [
    {
      id: 'REG001',
      sectorName: 'Registration & Reception',
      description: 'Main entrance registration and guest reception services',
      leader1: 'Rajesh Kumar',
      leader1Mobile: '+91 98765 43210',
      leader2: 'Priya Sharma',
      leader2Mobile: '+91 87654 32109',
      allocatedCount: 25,
      requiredCount: 30
    },
    {
      id: 'KIT001',
      sectorName: 'Kitchen & Food Service',
      description: 'Food preparation, cooking, and serving operations',
      leader1: 'Suresh Babu',
      leader1Mobile: '+91 76543 21098',
      leader2: 'Lakshmi Devi',
      leader2Mobile: '+91 65432 10987',
      allocatedCount: 40,
      requiredCount: 45
    },
    {
      id: 'SEC001',
      sectorName: 'Security & Crowd Management',
      description: 'Security operations and crowd control management',
      leader1: 'Ravi Shankar',
      leader1Mobile: '+91 54321 09876',
      leader2: 'Karthik Reddy',
      leader2Mobile: '+91 43210 98765',
      allocatedCount: 18,
      requiredCount: 25
    },
    {
      id: 'MED001',
      sectorName: 'Medical & First Aid',
      description: 'Medical assistance and first aid services',
      leader1: 'Dr. Meera Nair',
      leader1Mobile: '+91 32109 87654',
      leader2: 'Nurse Kavitha',
      leader2Mobile: '+91 21098 76543',
      allocatedCount: 12,
      requiredCount: 15
    },
    {
      id: 'TRA001',
      sectorName: 'Transport & Logistics',
      description: 'Transportation coordination and logistics support',
      leader1: 'Venkat Rao',
      leader1Mobile: '+91 10987 65432',
      leader2: 'Srinivas Murthy',
      leader2Mobile: '+91 09876 54321',
      allocatedCount: 22,
      requiredCount: 28
    },
    {
      id: 'DEC001',
      sectorName: 'Decoration & Stage',
      description: 'Stage decoration and event setup management',
      leader1: 'Arjun Prasad',
      leader1Mobile: '+91 98765 43211',
      leader2: 'Divya Krishnan',
      leader2Mobile: '+91 87654 32110',
      allocatedCount: 15,
      requiredCount: 20
    }
  ];

  // Mock data for allocated members
  const mockAllocatedMembers = {
    'REG001': [
      {
        id: 'M001',
        name: 'Rajesh Kumar',
        mobile: '+91 98765 43210',
        servicePoint: 'Main Gate Registration',
        district: 'Chennai District',
        samithi: 'Adyar Samithi',
        age: 35,
        gender: 'Male',
        bloodGroup: 'B+',
        email: 'rajesh.kumar@email.com',
        address: '123, Anna Nagar, Chennai - 600040',
        saiConnectId: 'SC001234',
        role: 'Volunteer',
        sector: 'Registration & Reception',
        allocationDate: '2025-01-10',
        serviceDescription: 'Handle main entrance registration and guest verification',
        qualification: 'B.Tech Computer Science',
        profession: 'Software Engineer',
        skills: ['Communication', 'Leadership', 'Computer Skills']
      },
      {
        id: 'M002',
        name: 'Priya Sharma',
        mobile: '+91 87654 32109',
        servicePoint: 'VIP Reception Desk',
        district: 'Chennai District',
        samithi: 'Mylapore Samithi',
        age: 28,
        gender: 'Female',
        bloodGroup: 'A+',
        email: 'priya.sharma@email.com',
        address: '456, T. Nagar, Chennai - 600017',
        saiConnectId: 'SC001235',
        role: 'Coordinator',
        sector: 'Registration & Reception',
        allocationDate: '2025-01-10',
        serviceDescription: 'Manage VIP guest reception and coordination',
        qualification: 'MBA Marketing',
        profession: 'Marketing Manager',
        skills: ['Public Relations', 'Event Management', 'Communication']
      }
    ],
    'KIT001': [
      {
        id: 'M003',
        name: 'Suresh Babu',
        mobile: '+91 76543 21098',
        servicePoint: 'Kitchen Preparation',
        district: 'Chennai District',
        samithi: 'Velachery Samithi',
        age: 42,
        gender: 'Male',
        bloodGroup: 'O+',
        email: 'suresh.babu@email.com',
        address: '789, Velachery, Chennai - 600042',
        saiConnectId: 'SC001236',
        role: 'Team Leader',
        sector: 'Kitchen & Food Service',
        allocationDate: '2025-01-10',
        serviceDescription: 'Supervise kitchen preparation and food quality',
        qualification: 'Hotel Management',
        profession: 'Chef',
        skills: ['Cooking', 'Team Management', 'Food Safety']
      }
    ]
  };

  const navigate = useNavigate();

  useEffect(() => {
    const role = localStorage.getItem('role');

    if (!role) {
      navigate('/login-screen');
    }

    if(role == 'basic' || role == 'leader') {
      navigate('/dashboard');
    }
    
  }, [navigate]);

  useEffect(() => {
    // Simulate API call to fetch sectors
    const fetchSectors = async () => {
      setLoading(true);
      setTimeout(() => {
        setSectors(mockSectors);
        setLoading(false);
      }, 1000);
    };

    const fetchData = async () => {

      try {

        setLoading(true);

        const data = await httpHelper(URL_CONSTANTS.CURRENT_BATCH, Method_TYPE.GET);

        setCurrentBatch(data._id.toString());

        setCurrentBatchObj(data);

        const sectorStats = await httpHelper(`${URL_CONSTANTS.SERVICE}/${data._id.toString()}/summary`, Method_TYPE.GET);

        setSectors(sectorStats); // Update the state with the new counts

        const stats = await  httpHelper(`${URL_CONSTANTS.STATS}/${data._id.toString()}/stats`, Method_TYPE.GET);

        setStatistics(stats);

        const groupLeaderInfo = await httpHelper(`${URL_CONSTANTS.GROUP_LEADER_INFO}`, Method_TYPE.GET);

        setGroupLeaders(groupLeaderInfo || []);

        setLoading(false);

      } catch (error) {

        console.error('Error fetching data:', error);

        setSectors([])

        setLoading(false)

      }
    };
 

    fetchData()

    //fetchSectors();
  }, []);

  const handleSectorSelect = async (sector) => {
   
    setSelectedSector(sector);
   
    setMembersLoading(true);

    setShowSectorInfo(false);

    const normalizedMembers = await getMembersForSector(sector._id);

    setAllocatedMembers(normalizedMembers);
    
    setMembersLoading(false);

  };

  const getMembersForSector = async (sectorId) => { 
   
    const memberDetails = await httpHelper(`${URL_CONSTANTS.SERVICE}/${currentBatch}/allocated/${sectorId}`, Method_TYPE.GET);
   return  memberDetails.map(memberObj => ({
      id: memberObj.member._id,
      name: memberObj.member.name,
      batch: memberObj?.batch || "----",
      saiOneId: memberObj.member.saiOneId,
      mobNo : memberObj.member.mobNoPrimary,
      age: calculateAge(memberObj.member.dob),
      district: memberObj.member?.district?.districtName || "------",
      samithi: memberObj.member?.samithi?.samithiName || "-----",
      samithiId : memberObj.member?.samithi?._id || "-----",
      districtId : memberObj.member?.district?._id || "-----",
      sector: memberObj.deploymentpoint?.sector || "----",
      deploymentpoint: memberObj.deploymentpoint?._id || "----",
      servicePoint: memberObj.deploymentpoint?.name || "----",
      servicePointId: memberObj._id || "----",
      serviceDescription: memberObj.deploymentpoint?.description || "----",
      bloodGroup: memberObj.member.bloodGroup || "----",
      printStatus : memberObj.printStatus || "Not Printed",
      is_blocked: Boolean(
        memberObj.member?.is_blocked === true ||
        memberObj.member?.is_blocked === 'true' ||
        memberObj.member?.is_blocked === 1 ||
        memberObj.member?.isBlocked === true ||
        memberObj.member?.isBlocked === 'true'
      ),
      rawMember: memberObj.member,
    }));

  }
  

  const handlePreview = (member) => {
    // setPreviewMember(member);
    const matchedLeader = groupLeaders.find(leader => leader.sector._id === member.sector);
    // setGroupLeader(matchedLeader || null);
    sessionStorage.setItem('pMember', JSON.stringify(member));
    sessionStorage.setItem('pGLeader', JSON.stringify(matchedLeader || null));
    sessionStorage.setItem('pCurrentBatch',JSON.stringify(currentBatchObj || null));
    window.open("/printCard", "_blank");
  };

  const handleRemoveService = async (member) => {
     setSelectedMember(member);
     setShowRemoveModal(true);
  }

  const handleUnallocate = async (member) => {
    const serviceId = member?.servicePointId;
    if (!serviceId || serviceId === '----') {
      alert('Unable to unallocate. Service id is missing.');
      return;
    }

    const confirmed = window.confirm(`Move ${member.name || 'this member'} to unallocated?`);
    if (!confirmed) return;

    const sectorId = selectedSector?._id;
    setUnallocatingServiceId(serviceId);
    try {
      await httpHelper(URL_CONSTANTS.SERVICE_UNALLOCATE, Method_TYPE.POST, { _id: serviceId });
    } catch (err) {
      console.error('Error unallocating service:', err);
      alert(err?.message || 'Problem in unallocating member. Please contact tech team.');
      setUnallocatingServiceId(null);
      return;
    }

    setShowSectorInfo(false);
    setAllocatedMembers((current) => current.filter((row) => row.servicePointId !== serviceId));

    try {
      if (sectorId) {
        const normalizedMembers = await getMembersForSector(sectorId);
        setAllocatedMembers(normalizedMembers);
      }

      if (currentBatch) {
        const [stats, sectorStats] = await Promise.all([
          httpHelper(`${URL_CONSTANTS.STATS}/${currentBatch}/stats`, Method_TYPE.GET),
          httpHelper(`${URL_CONSTANTS.SERVICE}/${currentBatch}/summary`, Method_TYPE.GET),
        ]);
        setStatistics(stats);
        setSectors(sectorStats);
      }
    } catch (err) {
      console.error('Unallocated, but refreshing the list failed:', err);
    } finally {
      setUnallocatingServiceId(null);
    }
  };

  const confirmRemoveMember = async (service) => {

      const data = {
        sector: service.sector,
        deploymentpoint: service.deploymentpoint,
        batch: service.batch,
        member: service.id,
        samithi : service.samithiId,
        district : service.districtId,
        serviceId : service.servicePointId,
        removedOn : service.removedOn,
        reason:service.reason,
      }
      try {
         await httpHelper(`${URL_CONSTANTS.SERVICE}removeService`, Method_TYPE.POST, data);
         
         alert("Member removed removed successfully")
         
         setShowRemoveModal(false);

         setMembersLoading(true);

         setShowSectorInfo(false);

         const normalizedMembers = await getMembersForSector(service.sector);

         setAllocatedMembers(normalizedMembers);
    
         setMembersLoading(false);

      }
      catch(err) {
         console.log("ERRR",err)
         alert("Problem in  removing member.Please contact tech team.")
      }
    
  }

  const handleGotoSWAlloc = () =>{
      setShowSectorInfo(true);
      setSelectedSector(null);
  }

  const handleRefreshMembers = async () => {
    if (!selectedSector?._id) return;

    setMembersLoading(true);
    try {
      const normalizedMembers = await getMembersForSector(selectedSector._id);
      setAllocatedMembers(normalizedMembers);
    } catch (error) {
      console.error('Error refreshing allocated members:', error);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleEditService = (member) => {
    setEditServiceMember(member);
    setPreviewMember(null); // Close preview modal if open
  };

  const handleSaveService = (updatedMember) => {
    // Update the member in the allocated members list
    setAllocatedMembers(prev =>
      prev.map(member =>
        member.id === updatedMember.id ? updatedMember : member
      )
    );

    // Update the sector's allocated count if needed
    setSectors(prev =>
      prev.map(sector =>
        sector.id === selectedSector._id ? { ...sector } : sector
      )
    );
  };

  const getTotalAllocated = () => {
    return statistics.allocated || 0;
  };

  const getTotalRequired = () => {
    return (statistics.unallocated || 0) + (statistics.required || 0);
  };

  return (
    <>
      <Helmet>
        <title>Allocated Members Management - Tamil Nadu Prasanthi Seva</title>
        <meta name="description" content="Manage sector-wise volunteer allocations and service assignments for Tamil Nadu Prasanthi Seva events" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main className="pt-8 pb-8">
          <div className="max-w-7xl mx-auto px-4 lg:px-6">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div>
                  <h1 className="font-heading font-semibold text-2xl text-foreground">
                    Allocated Members Management
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    View and manage sector-wise volunteer allocations and service assignments
                  </p>
                </div>
                
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 w-full sm:w-auto">
                  <div className="bg-card border border-border rounded-lg px-4 py-2 mb-3 sm:mb-0">
                    <div className="flex items-center space-x-4 text-sm">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-success rounded-full"></div>
                        <span className="text-muted-foreground">Allocated:</span>
                        <span className="font-semibold text-success">{getTotalAllocated()}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-warning rounded-full"></div>
                        <span className="text-muted-foreground">Required:</span>
                        <span className="font-semibold text-warning">{getTotalRequired()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sector Table */}
            {showSectorInfo && (<SectorTable
              sectors={sectors}
              onSectorSelect={handleSectorSelect}
              selectedSectorId={selectedSector?._id}
              loading={loading}
            />)}

            {/* Member Allocation Table */}
            {selectedSector && (
              <MemberAllocationTable
                members={allocatedMembers}
                sectorName={selectedSector.name}
                onPreview={handlePreview}
                onEditService={handleEditService}
                loading={membersLoading}
                onGotoSWAlloc={handleGotoSWAlloc}
                onRemoveService={handleRemoveService}
                onUnallocate={handleUnallocate}
                unallocatingServiceId={unallocatingServiceId}
                onRefresh={handleRefreshMembers}
              />
            )}

            {/* Empty State */}
            {/* {!selectedSector && !loading && (
              <div className="text-center py-12 mt-8">
                <Icon name="Users" size={64} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading font-medium text-lg text-foreground mb-2">
                  Select a Sector
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Choose a sector from the table above to view detailed member allocations and manage service assignments.
                </p>
              </div>
            )} */}
          </div>
        </main>

        {/* Modals */}
        <MemberPreviewModal
          member={previewMember}
          gLeader = {groupLeader}
          isOpen={!!previewMember}
          onClose={() => setPreviewMember(null)}
          onEditService={handleEditService}
        />

        <EditServiceModal
          member={editServiceMember}
          isOpen={!!editServiceMember}
          onClose={() => setEditServiceMember(null)}
          onSave={handleSaveService}
        />

        <RemoveMemberModal
              isOpen={showRemoveModal}
              onClose={() => {
                setShowRemoveModal(false);
                setSelectedMember(null);
              }}
              member={selectedMember}
              onConfirm={confirmRemoveMember}
              loading={actionLoading}
          />
         {/* <Footer /> */}
      </div>
    </>
  );
};

export default AllocatedMembersManagement;